import { useEffect, useRef } from 'react'

const BackgroundAnimation = () => {
    const canvasRef = useRef(null)

    useEffect(() => {
        const canvas = canvasRef.current
        const ctx = canvas.getContext('2d')
        let width = window.innerWidth
        let height = window.innerHeight

        canvas.width = width
        canvas.height = height

        let drones = []
        let bedParticles = []

        const droneCount = 3500
        const bedParticleCount = 600

        const mouse = { x: width / 2, y: height / 2 }

        class Particle {
            constructor(type) {
                this.type = type
                this.reset()
            }

            reset() {
                if (this.type === 'drone') {
                    this.x = (Math.random() - 0.5) * width * 3
                    this.y = (Math.random() - 0.5) * height * 5
                    this.z = Math.random() * 500
                    this.color = '#38bdf8'
                    this.size = 2.0 + Math.random() * 2.5
                    this.accel = 0.005
                } else {
                    // Bed Particles
                    this.x = (Math.random() - 0.5) * width * 4
                    this.y = 100 + Math.random() * height * 0.8
                    this.z = Math.random() * 2000
                    this.color = '#64748b'
                    this.size = 2.0 + Math.random() * 3.0
                    this.accel = 0.005
                    this.driftPhase = Math.random() * Math.PI * 2
                }

                this.vx = 0; this.vy = 0; this.vz = 0
                this.friction = 0.85

                this.tx = this.x; this.ty = this.y; this.tz = this.z
                this.assigned = false
                this.scale = 0
            }

            update(time) {
                if (this.type === 'bed') {
                    // Slower movement for bottom layer
                    this.z -= 0.5
                    if (this.z < -500) this.z = 2000

                    // Reduced lateral sway
                    this.x += Math.sin(time + this.z * 0.005 + this.driftPhase) * 0.3
                    this.y += Math.cos(time + this.x * 0.005) * 0.2

                    const mx = (mouse.x - width / 2) * 0.01
                    const my = (mouse.y - height / 2) * 0.01

                    this.px = (width / 2) + (this.x - mx) * (800 / (800 + this.z))
                    this.py = (height / 2) + (this.y - my) * (800 / (800 + this.z))
                    this.scale = 800 / (800 + this.z)

                } else {
                    // Drone Logic
                    let dx, dy, dz
                    if (this.assigned) {
                        dx = this.tx - this.x
                        dy = this.ty - this.y
                        dz = this.tz - this.z
                    } else {
                        dx = Math.sin(this.y * 0.01 + time) * 5
                        dy = Math.cos(this.x * 0.01 + time) * 5
                        dz = 0
                    }

                    this.vx += dx * this.accel
                    this.vy += dy * this.accel
                    this.vz += dz * this.accel

                    this.vx *= this.friction
                    this.vy *= this.friction
                    this.vz *= this.friction

                    this.x += this.vx
                    this.y += this.vy
                    this.z += this.vz

                    const perspective = 800 / (800 + this.z)
                    this.px = (width / 2) + this.x * perspective
                    this.py = (height / 2) + this.y * perspective
                    this.scale = perspective
                }
            }

            draw(ctx) {
                if (this.scale > 0) {
                    ctx.beginPath()
                    const alpha = this.type === 'bed' ? 0.3 : Math.min(1, this.scale * 1.5)
                    ctx.globalAlpha = alpha
                    ctx.fillStyle = this.color
                    ctx.arc(this.px, this.py, this.size * this.scale, 0, Math.PI * 2)
                    ctx.fill()
                }
            }
        }

        const getWaveTargets = (time) => {
            const targets = []
            const rows = 60
            const cols = 60
            const spacing = 40

            const offsetX = (cols * spacing) / 2
            const offsetZ = 700

            const my = (mouse.y - height / 2) * 0.05

            for (let r = 0; r < rows; r++) {
                for (let c = 0; c < cols; c++) {
                    const xBase = c * spacing - offsetX
                    const zBase = r * spacing - offsetZ

                    // Deterministic Jitter
                    // Break the perfect straight lines using pseudo-random offsets
                    // derived from row/col indices so they are stable
                    const jitterX = Math.sin(r * 12.9898 + c * 78.233) * (spacing * 0.7)
                    const jitterZ = Math.cos(r * 39.346 + c * 11.135) * (spacing * 0.7)

                    const x = xBase + jitterX
                    const z = zBase + jitterZ

                    // Main Wave - Faster & More movement
                    let y = Math.sin(x * 0.005 + time * 1.5) * 40
                        + Math.sin(z * 0.008 + time * 1.2) * 50

                    // Add extra vertical noise so the surface isn't smooth glass
                    y += Math.sin(r * 1.1 + c * 2.2) * 15

                    y -= 180

                    y += my

                    targets.push({
                        x: x,
                        y: y,
                        z: z + 700
                    })
                }
            }
            return targets
        }

        const init = () => {
            drones = []
            bedParticles = []
            for (let i = 0; i < droneCount; i++) drones.push(new Particle('drone'))
            for (let i = 0; i < bedParticleCount; i++) bedParticles.push(new Particle('bed'))
        }

        let time = 0

        const animate = () => {
            ctx.clearRect(0, 0, width, height)

            const gradient = ctx.createRadialGradient(width / 2, height / 2, 0, width / 2, height / 2, width)
            gradient.addColorStop(0, '#020617')
            gradient.addColorStop(0.7, '#0f172a')
            gradient.addColorStop(1, '#000000')
            ctx.fillStyle = gradient
            ctx.fillRect(0, 0, width, height)

            time += 0.01

            // Bottom Layer
            bedParticles.forEach(p => {
                p.update(time)
                p.draw(ctx)
            })

            // Top Wave
            let targets = getWaveTargets(time)
            let targetIndex = 0
            drones.forEach((drone) => {
                if (targetIndex < targets.length) {
                    drone.assigned = true
                    const t = targets[targetIndex]
                    drone.tx = t.x
                    drone.ty = t.y
                    drone.tz = t.z
                    targetIndex++
                } else {
                    drone.assigned = false
                    drone.tx = drone.x + Math.sin(time * 0.5 + drone.y) * 5
                    drone.ty = drone.y + Math.cos(time * 0.5 + drone.x) * 5
                }
                drone.update(time)
                drone.draw(ctx)
            })

            requestAnimationFrame(animate)
        }

        const handleResize = () => {
            width = window.innerWidth
            height = window.innerHeight
            canvas.width = width
            canvas.height = height
            init()
        }
        const handleMouseMove = (e) => {
            mouse.x = e.clientX
            mouse.y = e.clientY
        }

        init()
        animate()

        window.addEventListener('resize', handleResize)
        window.addEventListener('mousemove', handleMouseMove)
        return () => {
            window.removeEventListener('resize', handleResize)
            window.removeEventListener('mousemove', handleMouseMove)
        }
    }, [])

    return <canvas ref={canvasRef} style={{ display: 'block', position: 'fixed', top: 0, left: 0, zIndex: 1 }} />
}

export default BackgroundAnimation
